import { z } from "zod";

const translationPath = "chatBot.page.service.ticketServiceArea.errors.";

const actionSelectorSchema = z.object(
  {
    id: z.string(),
    name: z.string(),
    code: z.string(),
    actionName: z.string(),
  },
  { invalid_type_error: translationPath + "action_selector" },
);

const ticketServiceSchema = z.object(
  {
    id: z.string(),
    name: z.string(),
    code: z.string().optional().nullable(),
    externalChatSiteAppId: z.string().optional().nullable(),
    externalChatSiteAppName: z.string().optional().nullable(),
    externalChatSiteApp: z
      .object({
        id: z.string(),
        name: z.string(),
      })
      .optional()
      .nullable(),
  },
  { invalid_type_error: translationPath + "ticket_services" },
);

const externalChatSiteAppSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().optional().nullable(),
});

const ticketServiceArea = z.object(
  {
    id: z.string(),
    name: z.string(),
    code: z.string().optional().nullable(),
  },
  { invalid_type_error: translationPath + "ticket_service_area" },
);

const ticketChannelSchema = z.object(
  {
    id: z.string(),
    name: z.string(),
    code: z.string().optional().nullable(),
  },
  { invalid_type_error: translationPath + "ticket_channel" },
);

const ticketChannelStepSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().optional().nullable(),
});

const ticketDefaultDocSchema = z.object(
  {
    id: z.string(),
    name: z.string(),
    code: z.string().optional().nullable(),
  },
  { invalid_type_error: translationPath + "ticket_default_doc" },
);

const ticketEventTypeSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().optional().nullable(),
});

const ticketAutomationReceivedRuleFieldSchema = z.object({
  id: z.string(),
  name: z.string(),
});

const ticketEventSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().optional().nullable(),
  ticketEventType: ticketEventTypeSchema,
});

export const ticketServiceAreaSchema = z
  .object({
    id: z.string().optional(),
    enable: z.boolean(),
    enableAllTheTime: z.boolean(),
    enableSpecialTime: z.boolean(),
    isIdentification: z.boolean(),
    name: z
      .string()
      .min(1, translationPath + "name")
      .refine((val) => val.length <= 24, translationPath + "name_characteres"),
    code: z.string().min(1, translationPath + "code"),
    summary: z
      .string()
      .min(1, translationPath + "summary")
      .refine(
        (val) => val.length <= 72,
        translationPath + "summary_characteres",
      ),
    displayOrder: z.string().min(1, translationPath + "display_order"),
    actionSelector: actionSelectorSchema,
    ticketService: ticketServiceSchema,
    ticketServiceArea: ticketServiceArea.optional().nullable(),
    ticketChannel: ticketChannelSchema.optional().nullable(),
    ticketChannelStep: ticketChannelStepSchema.optional().nullable(),
    ticketDefaultDoc: ticketDefaultDocSchema.optional().nullable(),
    ticketEvent: ticketEventSchema.optional().nullable(),
    ticketEventType: ticketEventTypeSchema.optional().nullable(),
    ticketAutomationReceivedRule: ticketAutomationReceivedRuleFieldSchema
      .optional()
      .nullable(),
    externalChatSiteAppId: z.string().optional().nullable(),
    externalChatSiteApp: externalChatSiteAppSchema.optional().nullable(),
    fullScreenMode: z.boolean().optional(),
    sendProtocolNumber: z.boolean().optional(),
    showOutsideBusinessDay: z.boolean(),
    ticketChannelOutsideBusinessDay: ticketChannelSchema.optional().nullable(),
    ticketChannelStepOutsideBusinessDay: ticketChannelStepSchema
      .optional()
      .nullable(),
    messageOutsideBusinessDay: z
      .string()
      .refine(
        (val) => val.length <= 500,
        translationPath + "message_outside_characteres",
      )
      .optional()
      .nullable(),
    summaryOutsideBusinessDay: z
      .string()
      .refine(
        (val) => val.length <= 72,
        translationPath + "summary_outside_characteres",
      )
      .optional()
      .nullable(),
  })
  .superRefine((data, ctx) => {
    if (
      data.showOutsideBusinessDay &&
      !data.messageOutsideBusinessDay?.trim()
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["messageOutsideBusinessDay"],
        message: translationPath + "message_outside_business_day",
      });
    }
    if (
      data.ticketChannelOutsideBusinessDay &&
      !data.ticketChannelStepOutsideBusinessDay
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["ticketChannelStepOutsideBusinessDay"],
        message: translationPath + "ticket_channel_step",
      });
    }
  });

export type TTicketServiceAreaForm = z.infer<typeof ticketServiceAreaSchema>;
